import type { JSONSerializable } from '@highimpact-frontend/api-types';
import type { IntervalJSON } from '@highimpact-frontend/api-types';

import type { DecimationPolicy, TimeseriesTimeRange } from './types';

/** Deep-clones JSON-serializable values for isolated measure request bodies. */
export function cloneJsonSerializable<T extends JSONSerializable>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

/** Check if the time range is a simple start/end string type. */
export function isSimpleTimeRange(
  timeRange: TimeseriesTimeRange
): timeRange is { start: string; end: string } {
  return 'start' in timeRange && 'end' in timeRange && !('type' in timeRange);
}

/** Convert the time range to an IntervalJSON object for the /measures/v2 endpoint. */
export function timeRangeToInterval(
  timeRange: TimeseriesTimeRange
): IntervalJSON {
  if (isSimpleTimeRange(timeRange)) {
    return {
      type: 'fixed_time_range',
      name: null,
      startdate: timeRange.start,
      enddate: timeRange.end,
    };
  }

  return timeRange;
}

/** Convert the decimation policy to an API parameter for the /measures/v2 endpoint. */
export function decimationToApiParam(
  decimation?: DecimationPolicy
): { type: 'by_point'; points: number } | undefined {
  if (!decimation?.enabled) {
    return undefined;
  }

  return { type: 'by_point', points: decimation.points };
}
